import React, { useCallback, useEffect, useRef } from "react"
import { CommandItem } from "@ui/shad"
import {
  Building2,
  Contact as ContactIcon,
  Home,
  Landmark,
  MessageCircle,
  Shield,
  ShieldUser,
  Umbrella,
  User,
  UserPlus,
  Users,
  Wallet,
} from "lucide-react"

import { MentionOption, type MentionRecordType } from "./types"

interface MentionItemProps {
  option: MentionOption
  isSelected: boolean
  onClick: () => void
}

export function MentionItem({ option, isSelected, onClick }: MentionItemProps) {
  const itemRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isSelected && itemRef.current) {
      itemRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" })
    }
  }, [isSelected])

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleSelect = useCallback(() => {
    onClick()
  }, [onClick])

  const renderIcon = (recordType: MentionRecordType) => {
    const iconClass = "text-muted-foreground h-4 w-4 shrink-0"

    switch (recordType) {
      case "Trust":
        return <Shield className={iconClass} />
      case "Client":
        return <ShieldUser className={iconClass} />
      case "Prospect":
        return <UserPlus className={iconClass} />
      case "Household":
        return <Home className={iconClass} />
      case "Business":
        return <Building2 className={iconClass} />
      case "Contact":
        return <ContactIcon className={iconClass} />
      case "Person":
      case "User":
        return <User className={iconClass} />
      case "SMSMessage":
        return <MessageCircle className={iconClass} />
      case "CustodianAccount":
        return <Landmark className={iconClass} />
      case "FinancialAccount":
        return <Wallet className={iconClass} />
      case "InsurancePolicy":
        return <Umbrella className={iconClass} />
      default:
        return <Users className={iconClass} />
    }
  }

  return (
    <CommandItem
      ref={itemRef}
      key={option.recordId}
      onSelect={handleSelect}
      onMouseDown={handleMouseDown}
      className={`flex cursor-pointer items-center gap-2 px-2 py-1.5 ${isSelected ? "bg-accent text-accent-foreground" : "bg-background hover:bg-accent/50"}`}
      disableHighlighting={true}
    >
      {renderIcon(option.recordType)}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{option.label}</div>
      </div>
    </CommandItem>
  )
}
